import React, { useState, useRef } from 'react'
import ImageUpload from './ImageUpload'
import DocumentUpload from './DocumentUpload'
import EstimateResult from './EstimateResult'
import { getTrackingParams, getApiBaseUrl } from '../utils/tracking'

const API_BASE = getApiBaseUrl()
const REQUEST_TIMEOUT_MS = 60000 // 60 seconds
const MAX_RETRIES = 2

function StorageEstimator() {
  const [description, setDescription] = useState('')
  const [images, setImages] = useState([])
  const [imageUrls, setImageUrls] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Calculating...')
  const [error, setError] = useState(null)
  const [errorDetails, setErrorDetails] = useState(null)
  const [result, setResult] = useState(null)
  const [tracking] = useState(() => getTrackingParams())
  const abortControllerRef = useRef(null)

  // Fetch with timeout support
  const fetchWithTimeout = async (url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
    abortControllerRef.current = new AbortController()
    const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), timeoutMs)

    try {
      const response = await fetch(url, {
        ...options,
        signal: abortControllerRef.current.signal,
      })
      clearTimeout(timeoutId)
      return response
    } catch (err) {
      clearTimeout(timeoutId)
      if (err.name === 'AbortError') {
        throw new Error(`Request timed out after ${timeoutMs / 1000} seconds`)
      }
      throw err
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!description.trim() && images.length === 0 && documents.length === 0) {
      setError('Please describe your items, upload photos, or attach documents.')
      setErrorDetails(null)
      return
    }

    setLoading(true)
    setLoadingMessage('Uploading files...')
    setError(null)
    setErrorDetails(null)
    setResult(null)

    let retryCount = 0

    while (retryCount <= MAX_RETRIES) {
      try {
        // Upload images first if any
        const totalUploads = images.length + documents.length
        let uploadIndex = 0

        const uploadedImageUrls = []
        for (let i = 0; i < images.length; i++) {
          uploadIndex++
          setLoadingMessage(`Uploading file ${uploadIndex}/${totalUploads}...`)
          const url = await uploadFile(images[i])
          uploadedImageUrls.push(url)
        }

        // Upload documents
        const uploadedDocUrls = []
        for (let i = 0; i < documents.length; i++) {
          uploadIndex++
          setLoadingMessage(`Uploading file ${uploadIndex}/${totalUploads}...`)
          const url = await uploadFile(documents[i])
          uploadedDocUrls.push(url)
        }

        // Make estimate request
        setLoadingMessage('Calculating estimate...')
        const response = await fetchWithTimeout(`${API_BASE}/estimate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            description: description.trim(),
            image_urls: uploadedImageUrls,
            document_urls: uploadedDocUrls,
            tracking: { ...tracking, source: 'frontend' },
          }),
        })

        if (!response.ok) {
          let errorDetail = ''
          try {
            const errBody = await response.text()
            try {
              const errJson = JSON.parse(errBody)
              errorDetail = errJson.detail || errBody
            } catch {
              errorDetail = errBody
            }
          } catch {
            errorDetail = response.statusText
          }
          throw new Error(`HTTP ${response.status}: ${errorDetail}`)
        }

        const data = await response.json()
        setResult(data)
        setLoading(false)
        return // Success

      } catch (err) {
        if (err.message.includes('timed out') && retryCount < MAX_RETRIES) {
          retryCount++
          setLoadingMessage(`Retrying (${retryCount}/${MAX_RETRIES})...`)
          continue
        }

        // Final failure
        const isTimeout = err.message.includes('timed out')
        const isNetwork = err.message.includes('Failed to fetch') || err.message.includes('NetworkError')

        if (isTimeout) {
          setError('Request timed out. The server took too long to respond.')
          setErrorDetails(`Attempted ${retryCount + 1} time(s). Try again or check your network connection.`)
        } else if (isNetwork) {
          setError('Network error. Unable to reach the server.')
          setErrorDetails(`API URL: ${API_BASE}\nCheck that the server is running and accessible.`)
        } else if (err.message.startsWith('HTTP ')) {
          setError(err.message)
          setErrorDetails(`API URL: ${API_BASE}/estimate`)
        } else {
          setError(err.message || 'An error occurred. Please try again.')
          setErrorDetails(null)
        }
        setLoading(false)
        return
      }
    }
  }

  const uploadFile = async (file) => {
    // Get presigned URL
    const presignResponse = await fetchWithTimeout(`${API_BASE}/uploads/presign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type
          || (/\.heic$/i.test(file.name) ? 'image/heic' : '')
          || (/\.heif$/i.test(file.name) ? 'image/heif' : ''),
      }),
    })

    if (!presignResponse.ok) {
      const text = await presignResponse.text()
      throw new Error(`Upload failed (HTTP ${presignResponse.status}): ${text}`)
    }

    const { upload_url, public_url } = await presignResponse.json()

    // Upload file
    const uploadResponse = await fetchWithTimeout(upload_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    })

    if (!uploadResponse.ok) {
      throw new Error(`File upload failed (HTTP ${uploadResponse.status})`)
    }

    return public_url
  }

  const handleClear = () => {
    setDescription('')
    setImages([])
    setImageUrls([])
    setDocuments([])
    setResult(null)
    setError(null)
    setErrorDetails(null)
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  return (
    <div className="space-y-6">
      {/* Disclaimer Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Best-effort AI estimate—talk to a teammate if unsure.
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Describe your items
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., 10 moving boxes, a queen bed frame (disassembled), 2 nightstands, a 55-inch TV..."
            className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            maxLength={20000}
          />
          <p className="mt-1 text-xs text-gray-500">
            {description.length}/20000 characters
          </p>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload photos (optional)
          </label>
          <ImageUpload
            images={images}
            onImagesChange={setImages}
            maxImages={10}
          />
        </div>

        {/* Document Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attach documents (optional)
          </label>
          <DocumentUpload
            documents={documents}
            onDocumentsChange={setDocuments}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-medium">{error}</p>
            {errorDetails && (
              <pre className="mt-2 text-xs text-red-700 whitespace-pre-wrap overflow-x-auto max-h-32">
                {errorDetails}
              </pre>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {loadingMessage}
              </span>
            ) : (
              'Get Estimate'
            )}
          </button>

          {(result || description || images.length > 0 || documents.length > 0) && (
            <button
              type="button"
              onClick={handleClear}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Result */}
      {result && <EstimateResult result={result} />}
    </div>
  )
}

export default StorageEstimator
